// How the brand portal writes campaign facts: money, prices, statuses, dates,
// initials. Shared by the pages and the campaign table.

import {amountForMarket} from '../../domain/market-rates';
import type {MarketRate} from '../../shared/api';
import type {BrandCampaign, DeliveryMode} from '../../shared/brand-api';

export function money(cents: number, currency: string): string {
  return new Intl.NumberFormat(currency === 'HUF' ? 'hu-HU' : 'en-US', {style: 'currency', currency, maximumFractionDigits: currency === 'HUF' ? 0 : 2}).format(cents / 100);
}

export function rateLabel(base: number, rates: readonly MarketRate[], countries: readonly string[], currency: string): string {
  if (countries.length > 1) return `${countries.length} local prices`;
  const price = amountForMarket(base, currency, rates, countries[0] || 'US');
  return money(price.amount_cents, price.currency);
}

/** A campaign's state in words; "rejected" means we sent it back with changes to make. */
export const CAMPAIGN_STATUS: Record<BrandCampaign['status'], string> = {
  draft: 'Draft',
  in_review: 'In review',
  rejected: 'Changes needed',
  active: 'Active',
  paused: 'Paused',
  completed: 'Finished',
};

/** Where a creator is with the campaign, from their reservation. */
export function participantStatus(status: string | null): string {
  const LABEL: Record<string, string> = {
    reserved: 'Reserved', in_progress: 'Working on it', submitted: 'Videos handed in', completed: 'Done',
    released: 'Released', expired: 'Expired', cancelled: 'Cancelled',
  };
  return status ? LABEL[status] ?? status.replace(/_/g, ' ') : 'Joined';
}

/** Reservations that still hold a spot or finished the work, not the ones given back. */
export const HOLDING = new Set(['reserved', 'in_progress', 'submitted', 'completed']);

/** A campaign made before canvas accounts that already ran on our logins keeps its old name. */
export function workMode(mode: DeliveryMode, accountMode: 'team' | 'canvas' = 'canvas'): string {
  if (mode === 'file_only') return 'Create & hand off';
  // One name for both (Brúnó, 2026-10-16): the brand never needs to tell whose login it was.
  if (mode === 'managed_account') return 'Campaign account';
  return 'Create & post on own account (legacy)';
}

export function shortDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'}) : 'Not set';
}

export function initials(name: string, email: string): string {
  const bits = name.trim().split(/\s+/).filter(Boolean);
  return (bits.length > 1 ? `${bits[0]![0]}${bits.at(-1)![0]}` : (bits[0] || email).slice(0, 2)).toUpperCase();
}
